import {
  BookingAdvanceUnit,
  Client,
  CustomerContactPreference,
  DepositType,
  PlanFeature,
} from '../dataModels/models';
import { API_MESSAGES } from '../constants/ApiConstants';
import { buildApiUrl, requestJson } from './apiClient';

export type PublicBookingContact = {
  phone: string | null;
  email: string | null;
  address: string | null;
};

export type PublicBookingPlan = {
  planId: string;
  features: PlanFeature[];
};

export type PublicCustomerContactSettings = {
  customerContactPreference: CustomerContactPreference;
  customerContactEmail: string | null;
  customerContactPhone: string | null;
};

export type PublicBookingSettings = {
  advanceBookingValue: number;
  advanceBookingUnit: BookingAdvanceUnit;
  appointmentInstructions: string | null;
};

export type PublicBookingBusiness = {
  name: string;
  timezone: string;
  logoUrl: string | null;
  contact: PublicBookingContact | null;
  plan: PublicBookingPlan | null;
  notificationSettings: PublicCustomerContactSettings | null;
  bookingSettings: PublicBookingSettings | null;
};

export type PublicManageBusiness = {
  name: string;
  timezone: string;
  notificationSettings: PublicCustomerContactSettings | null;
};

export type PublicBranding = {
  colorSchemeName: string | null;
  fontThemeName: string | null;
  logoUrl: string | null;
  headerStyle: string | null;
};

export type PublicBookingProvider = {
  id: string;
  name: string;
};

export type PublicNamedProvider = {
  name: string;
};

export type PublicBookingService = {
  id: string;
  name: string;
  description: string;
  durationMinutes: number;
  price: number;
  currency: string;
};

export type PublicManageService = {
  id: string;
  name: string;
};

export type PublicNamedService = {
  name: string;
};

export type PublicBookingPaymentSummary = {
  depositRequired: boolean;
  paymentReady: boolean;
  depositType: DepositType;
  depositValue: number;
  isRefundable: boolean;
  refundWindowHours: number;
};

export type PublicBookingPageResponse = {
  bookingSlug: string;
  business: PublicBookingBusiness;
  branding: PublicBranding | null;
  provider: PublicBookingProvider;
  services: PublicBookingService[];
  paymentSummary: PublicBookingPaymentSummary;
};

export type PublicAvailabilityResponse = {
  bookingSlug: string;
  businessId: string;
  providerId: string;
  serviceId: string;
  month: string;
  timezone: string;
  availableTimesByDate: Record<string, string[]>;
  availableTimesByServiceId?: Record<string, Record<string, string[]>>;
};

export type CreatePublicAppointmentPayload = {
  providerId?: string;
  serviceId: string;
  date: string;
  time: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  marketingConsent: boolean;
  smsConsent: boolean;
  paymentIntentId?: string;
  clientNotes?: string;
};

export type FinalizePublicDepositPayload = {
  paymentIntentId: string;
  appointmentId: string;
};

export type AbandonPublicDepositPayload = {
  appointmentId: string;
  depositClientSecret: string;
  abandonDeposit: true;
};

export type AbandonPublicDepositResponse = {
  abandoned: boolean;
  appointmentFinalized: boolean;
};

export type CreatePublicAppointmentResponse = {
  appointment: {
    id: string;
    start: string;
  };
  client: {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
  };
  provider: PublicBookingProvider;
  service: PublicBookingService;
  business: PublicBookingBusiness;
  requiresPayment: boolean;
  publishableKey: string | null;
  paymentDetails: {
    stripeConnectedAccountId: string | null;
    depositClientSecret: string | null;
    depositPaymentIntentId: string | null;
    depositAmount: number;
  } | null;
};

export type PublicManageAppointmentResponse = {
  appointment: {
    start: string;
  };
  client: {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    allowEmail: boolean;
    allowSms: boolean;
  };
  provider: PublicNamedProvider;
  service: PublicManageService;
  business: PublicManageBusiness;
  canCancel: boolean;
  canReschedule: boolean;
  message: string;
};

export type PublicAppointmentConfirmationResponse = {
  appointment: {
    start: string;
  };
  provider: PublicNamedProvider;
  service: PublicNamedService;
  business: PublicManageBusiness;
  confirmed: boolean;
  message: string;
};

export type PublicEmailUnsubscribeResponse = {
  bookingSlug: string;
  client: Client;
  unsubscribed: boolean;
  message: string;
};

export type CancelManagedAppointmentPayload = {
  token: string;
};

export type RescheduleManagedAppointmentPayload = {
  token: string;
  serviceId: string;
  date: string;
  time: string;
};

export type RescheduleManagedAppointmentResponse = {
  provider: PublicBookingProvider;
  service: PublicBookingService;
  business: PublicBookingBusiness;
};

export type ConfirmPublicAppointmentPayload = {
  token: string;
};

type PublicRequestOptions = Omit<RequestInit, 'body'> & {
  payload?: unknown;
};

async function publicRequest<TResponse>(path: string, options?: PublicRequestOptions): Promise<TResponse> {
  const { response, body: responseBody } = await requestJson<TResponse>(buildApiUrl(path), {
    ...options,
  });

  if (!response.ok) {
    throw new Error(responseBody?.message ?? API_MESSAGES.PUBLIC_BOOKING_REQUEST_FAILED);
  }

  return (responseBody ?? {}) as TResponse;
}

export function getPublicBookingPage(bookingSlug: string) {
  return publicRequest<PublicBookingPageResponse>(`/public/booking/${encodeURIComponent(bookingSlug)}`);
}

export function getPublicBookingAvailability(bookingSlug: string, serviceId: string, month?: string, providerId?: string) {
  const searchParams = new URLSearchParams({
    serviceId,
  });

  if (month) {
    searchParams.set('month', month);
  }

  if (providerId) {
    searchParams.set('providerId', providerId);
  }

  return publicRequest<PublicAvailabilityResponse>(
    `/public/booking/${encodeURIComponent(bookingSlug)}/availability?${searchParams.toString()}`,
  );
}

export function getPublicBookingAvailabilityBatch(
  bookingSlug: string,
  serviceIds: string[],
  month: string,
  providerId?: string,
) {
  const searchParams = new URLSearchParams({
    serviceIds: serviceIds.join(','),
    month,
  });

  if (providerId) {
    searchParams.set('providerId', providerId);
  }

  return publicRequest<PublicAvailabilityResponse>(
    `/public/booking/${encodeURIComponent(bookingSlug)}/availability?${searchParams.toString()}`,
  );
}

export function createPublicAppointment(bookingSlug: string, payload: CreatePublicAppointmentPayload) {
  return publicRequest<CreatePublicAppointmentResponse>(`/public/booking/${encodeURIComponent(bookingSlug)}/appointments`, {
    method: 'POST',
    payload,
  });
}

export function finalizePublicDeposit(bookingSlug: string, payload: FinalizePublicDepositPayload) {
  return publicRequest<CreatePublicAppointmentResponse>(`/public/booking/${encodeURIComponent(bookingSlug)}/appointments`, {
    method: 'POST',
    payload,
  });
}

export function abandonPublicDeposit(bookingSlug: string, payload: AbandonPublicDepositPayload) {
  return publicRequest<AbandonPublicDepositResponse>(`/public/booking/${encodeURIComponent(bookingSlug)}/appointments`, {
    method: 'POST',
    payload,
  });
}

export function getManagedPublicAppointment(bookingSlug: string, token: string) {
  const searchParams = new URLSearchParams({ token });
  return publicRequest<PublicManageAppointmentResponse>(
    `/public/booking/${encodeURIComponent(bookingSlug)}/appointments/manage?${searchParams.toString()}`,
  );
}

export function confirmPublicAppointment(bookingSlug: string, payload: ConfirmPublicAppointmentPayload) {
  return publicRequest<PublicAppointmentConfirmationResponse>(
    `/public/booking/${encodeURIComponent(bookingSlug)}/appointments/confirm`,
    {
      method: 'POST',
      payload,
    },
  );
}

export function unsubscribePublicEmail(bookingSlug: string, token: string) {
  const searchParams = new URLSearchParams({ token });
  return publicRequest<PublicEmailUnsubscribeResponse>(
    `/public/booking/${encodeURIComponent(bookingSlug)}/email/unsubscribe?${searchParams.toString()}`,
  );
}

export function cancelManagedPublicAppointment(bookingSlug: string, payload: CancelManagedAppointmentPayload) {
  return publicRequest<PublicManageAppointmentResponse>(
    `/public/booking/${encodeURIComponent(bookingSlug)}/appointments/manage/cancel`,
    {
      method: 'POST',
      payload,
    },
  );
}

export function rescheduleManagedPublicAppointment(bookingSlug: string, payload: RescheduleManagedAppointmentPayload) {
  return publicRequest<RescheduleManagedAppointmentResponse>(
    `/public/booking/${encodeURIComponent(bookingSlug)}/appointments/manage/reschedule`,
    {
      method: 'POST',
      payload,
    },
  );
}
